import { forwardRef, useCallback, useImperativeHandle, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router';

export interface AccessibleMenuItem {
  key: string;
  label: string;
  href?: string;
  current?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
}

export interface AccessibleMenuHandle {
  focusActive(): void;
}

interface AccessibleMenuProps {
  label: string;
  items: AccessibleMenuItem[];
  className?: string;
  itemClassName?: string;
  onEscape?: () => void;
  onSelect?: () => void;
}

/**
 * Menú APG de una sola fila o columna. Esta pieza es la única responsable de
 * flechas, Inicio, Fin, búsqueda por inicial y tabulación rotativa.
 */
export const AccessibleMenu = forwardRef<AccessibleMenuHandle, AccessibleMenuProps>(function AccessibleMenu(
  { label, items, className, itemClassName, onEscape, onSelect },
  forwardedRef,
) {
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const elements = useCallback(() => (
    Array.from(listRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? [])
  ), []);

  const focusItem = useCallback((requestedIndex: number) => {
    const available = elements();
    if (!available.length) return;
    const next = (requestedIndex + available.length) % available.length;
    setActiveIndex(next);
    available[next]?.focus();
  }, [elements]);

  useImperativeHandle(forwardedRef, () => ({
    focusActive: () => focusItem(activeIndex),
  }), [activeIndex, focusItem]);

  const handleKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const available = elements();
    if (!available.length) return;
    const current = available.indexOf(document.activeElement as HTMLElement);
    const move = (next: number) => { event.preventDefault(); focusItem(next); };

    if (event.key === 'Escape') {
      event.preventDefault();
      onEscape?.();
    } else if (event.key === 'Home') move(0);
    else if (event.key === 'End') move(available.length - 1);
    else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') move(current + 1);
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') move(current - 1);
    else if (!event.altKey && !event.ctrlKey && !event.metaKey && event.key.length === 1 && /\S/.test(event.key)) {
      const initial = event.key.toLocaleLowerCase('es');
      for (let step = 1; step <= available.length; step += 1) {
        const index = (Math.max(current, -1) + step) % available.length;
        if ((available[index]?.textContent ?? '').trim().toLocaleLowerCase('es').startsWith(initial)) {
          move(index);
          break;
        }
      }
    }
  };

  const menuItemProps = (index: number) => ({
    role: 'menuitem' as const,
    tabIndex: activeIndex === index ? 0 : -1,
    onFocus: () => setActiveIndex(index),
  });

  return <ul ref={listRef} className={className} role="menu" aria-label={label} onKeyDown={handleKeyDown}>
    {items.map((item, index) => <li className={itemClassName} role="none" key={item.key}>
      {item.href?.startsWith('/') ? <Link {...menuItemProps(index)} className="nav-link" to={item.href} aria-current={item.current ? 'page' : undefined} onClick={onSelect}>{item.label}</Link>
        : item.href ? <a {...menuItemProps(index)} className="nav-link" href={item.href} aria-current={item.current ? 'page' : undefined} onClick={onSelect}>{item.label}</a>
          : <button {...menuItemProps(index)} className="nav-link" type="button" disabled={item.disabled} onClick={() => { item.onSelect?.(); onSelect?.(); }}>{item.label}</button>}
    </li>)}
  </ul>;
});
